// Copied from coulomb-commons-ui soh-band-migration 0.1.0 (src/styles/tokens.js),
// trimmed to what BucketSankeyChart uses.
//
// Colours, type and the canvas geometry the chart is laid out in. See
// sankeyGeometry.js for how the d3-sankey SVG is mapped onto that geometry.

import { ThemeProperties } from "../../Theme/ThemeProperties";

// Mapped onto the closest existing theme tokens.
export const COLOR = {
  ink: ThemeProperties.alertCard_ink,
  ink3: ThemeProperties.alertCard_ink3,
  ink4: ThemeProperties.alertCard_ink4,
  line: ThemeProperties.alertCard_line,
  paper2: ThemeProperties.purpleLight,
  crimson: ThemeProperties.alertCard_deleteRed,
  emerald: ThemeProperties.condIcon_resolution,
};

// Fill for a bucket / node / ribbon that has no colour of its own (e.g. a bucket
// label the org meta does not define).
export const UNKNOWN_BUCKET_COLOR = "#CCCCCC";

// The app font, so the chart text matches the rest of the page.
export const FONT_SANS = "inherit";

// ---- Canvas (source of truth) -----------------------------------------------
// Everything is drawn inside one CANVAS.W x CANVAS.H box and the HTML labels are
// placed as percentages of that box ("canvas units"). The box keeps this aspect ratio
// and its width scales with the card (at a ~1100px card 1 horizontal unit is ~0.85px).
//
// The HEIGHT does not scale: the box is CANVAS.H px tall at every card width, so one
// vertical canvas unit is exactly 1px (CANVAS_UNITS_PER_PX_Y). It used to keep the
// 1300 x H aspect ratio, which made the chart ~0.48 x the card width tall (~770px on a
// 1600px card) and pushed it past the bottom of a laptop screen.
//
// Vertical (top to bottom): plot [plotTop, plotBottom], then the month axis labels
// starting AXIS_GAP below the plot, then only the axis label's own height. No other
// margin: the legend and caption are HTML rows outside the box.
// Horizontal: xStart leaves room for the first month label (centred on its column);
// xEnd leaves room for the last column's count labels on the right.
const PLOT_TOP = 12;            // room for a count label nudged to the very top
const PLOT_HEIGHT = 380;        // px; fits 10 buckets with their gaps and minimum sizes
const AXIS_GAP = 10;            // plot bottom -> month label top, px
const AXIS_LABEL_H = 22;        // month label line (12px text), px
const AXIS_BOTTOM_MARGIN = 2;

/** Canvas units per px vertically: the box is CANVAS.H px tall. */
export const CANVAS_UNITS_PER_PX_Y = 1;

export const CANVAS = {
  W: 1300,
  H: PLOT_TOP + PLOT_HEIGHT + AXIS_GAP + AXIS_LABEL_H + AXIS_BOTTOM_MARGIN,   // 426 (px)
  plotTop: PLOT_TOP,                                                         // 12
  plotBottom: PLOT_TOP + PLOT_HEIGHT,                                        // 392
  gap: 16,             // fixed vertical gap between bucket slots in a column, px
  xStart: 48,          // first column centre
  xEnd: 1170,          // last column centre (leaves ~119 units for its count labels)
  nodeW: 14,           // rect width
  nodeLeftOfCentre: 8, // rect x = colX - 8
  minSlotH: 2,         // slot kept by a bucket that is empty in one column
  monthLabelY: PLOT_TOP + PLOT_HEIGHT + AXIS_GAP,                            // 402 (label top)
  monthLabelH: AXIS_LABEL_H,
};

// ---- d3-sankey canvas, in canvas units --------------------------------------
// The SVG viewBox is (VIEW_W + 2*SPACING) x PLOT_H and its host sits at
// (HOST_BOX.left, plotTop), the same size in canvas units, so 1 viewBox unit ==
// 1 canvas unit and viewBox y 0 is the plot top.
export const NODE_WIDTH = CANVAS.nodeW;
export const SPACING = 16;
export const PLOT_H = CANVAS.plotBottom - CANVAS.plotTop;            // 380
export const VIEW_W = CANVAS.xEnd - CANVAS.xStart + CANVAS.nodeW;    // 1136

// Where the d3-sankey SVG host sits inside the box.
export const HOST_BOX = {
  left: CANVAS.xStart - CANVAS.nodeLeftOfCentre - SPACING,  // 24
  top: CANVAS.plotTop,                                      // 12
  width: VIEW_W + 2 * SPACING,                              // 1168
  height: PLOT_H,                                           // 580
};

// Ribbons are the source bucket colour at this opacity over the white card.
export const EDGE_OPACITY = 0.45;

// Minimum distance between adjacent column (month) centres on screen, px. When the
// columns need more width than the card has, the chart widens and pans (drag, shift +
// wheel, trackpad, arrow keys) instead of squeezing them. 140px holds the widest
// inline count label ("12345 (12.4%)", ~75px, with 20% slack for font differences)
// beside a 12px bar and still leaves ~40px of ribbon visible between the label and
// the next column.
export const MIN_COLUMN_GAP_PX = 140;

export const TYPE = {
  monthLabel: { fontSize: "12px", fontWeight: 700, color: COLOR.ink4 },
  countLabel: { fontSize: "11px", fontWeight: 700, color: COLOR.ink },
  countShare: { fontSize: "10px", fontWeight: 400, color: COLOR.ink3 },
  legendItem: { fontSize: "12px", color: COLOR.ink3 },
  caption: { fontSize: "11px", color: COLOR.ink4 },
};

export const DOT = {
  legend: { width: 9, height: 9, borderRadius: "50%" },
};

// Legend: a row above the plot.
export const LEGEND_ROW = {
  display: "flex",
  gap: "8px 16px",
  flexWrap: "wrap",
  justifyContent: "flex-start",
  marginBottom: "12px",
};

// Caption: one muted line under the month axis.
export const CAPTION_ROW = {
  marginTop: "8px",
  fontSize: TYPE.caption.fontSize,
  color: TYPE.caption.color,
};
